import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import EstensaModel, { ImagingOpenParams } from 'estensa-module';
import { cloneDeep } from 'lodash-es';
import { Observable, firstValueFrom, fromEvent, map } from 'rxjs';
import { ButtonClick } from '../../../dialogs/se-modalwindow/se-modalwindow.component';
import { IRadioEprEventEx } from '../../../models/patient-history.models';
import { seSetModalPdfConfig, seShowPdfOnModal } from '../../../pdf-modal/redux/pdf-modal.actions';
import { seReportImportConclusions } from '../../../report/report.actions';
import { seOpenImagesByEprEvent } from '../../../root-store/root.actions';
import { ImagingService } from '../../../services/imaging/imaging.service';
import { ModalDialogNotificationService, NotificationService, NotificationType } from '../../../services/notification.service';
import { RadioSettingsService } from '../../../services/settings/radio-settings.service';
import { IReportHistoryEprEvent } from '../se-side-report-history/models/se-side-report-history.models';
import { EstensaWaitingService } from '../../../services/waiting.service';
import { PdfService } from '../../../services/pdf.service';

@Injectable({ providedIn: 'root' })
export class HistoryItemHelperService {
    constructor(
        private store: Store,
        private imagingService: ImagingService,
        private radioSettingsService: RadioSettingsService,
        private translate: TranslateService,
        private modalDialogNotificationService: ModalDialogNotificationService,
        private waitingService: EstensaWaitingService,
        private pdfService: PdfService,
        private notificationService: NotificationService
    ) { }

    copyReport(event: any, item: IRadioEprEventEx | IReportHistoryEprEvent | any) { // @TODO c'è any perchè sono da verificare/modificare i tipi
        event.stopPropagation();
        this.store.dispatch(seReportImportConclusions({ conclusions: item.report ?? item.text }));
    }

    async openPdf(event: MouseEvent, item: IRadioEprEventEx | IReportHistoryEprEvent) {
        event.stopPropagation();
   

        if (!item.isExternal) {
            this.store.dispatch(seShowPdfOnModal({ documentId: item.reportKey /* SOP Instace Uid */ }));
        } else {

            try {
                const printReport = (item as IReportHistoryEprEvent).printReport ?? null;
                if (!item.printingReportUrl && !printReport)
                    throw new Error(this.translate.instant('ErrorExternalReportUrlNotAvailable')); // L'URL (printReportUrl) non è disponibile."

                this.waitingService.isWaiting = true;
                const urlExternal = item.printingReportUrl ?? printReport; //printReportUrl se storia clinica, printReport se storico referti
                const pdfBuffer = await firstValueFrom(this.pdfService.getPdf(urlExternal));
                const blobPdf = new Blob([pdfBuffer], { type: 'application/pdf' });
                const blobString = await firstValueFrom(this.convertBlobToString(blobPdf));
                const cleanBase64 = blobString.replace(/^data:.*;base64,/, '');
                this.store.dispatch(seSetModalPdfConfig({ pdf: cleanBase64, print: false, visible: true }));
            }
            catch (e: any) {
                // "Impossibile recuperare il referto PDF esterno."
                const _error = this.translate.instant('ErrorRetrievingExternalPdfReport');
                const _details = e?.message ? `${e.message}` : `${e}`;
                console.error(_error + ` ${e}`);

                this.modalDialogNotificationService.error(_error);
                this.notificationService.error(_details, _error);
            }
            finally {
                if (this.waitingService.isWaiting)
                    this.waitingService.isWaiting = false;
            }
        }
    }

    private convertBlobToString(blob: Blob): Observable<string> {
        let reader = new FileReader();
        reader.readAsDataURL(blob);
        return fromEvent(reader, 'load').pipe(
            map(
                (result) => {
                    return reader.result as string;
                }
            )
        );
    }

    async openImagesByEprEvent(event: any, item: IRadioEprEventEx | IReportHistoryEprEvent) {
        event.stopPropagation();

        const settings = await firstValueFrom(this.radioSettingsService.radioSettingsAsync);

        // Verifico se la action è compatibile con il viewer selezionato (la append non la fanno tutti)
        const reportImageActionType: EstensaModel.ImageActionType =
            this.imagingService.checkViewerForAppendSupport(settings.reportSettings.reportImageActionType);

        let discardPreviousImages = false;
        switch (reportImageActionType) {
            case EstensaModel.ImageActionType.Ask:
                if (await this.imagingService.areImagesOpen()) {
                    let buttonClick: ButtonClick = await firstValueFrom(
                        this.modalDialogNotificationService.notify({
                            buttonYes: true,
                            buttonNo: true,
                            buttonCancel: false,
                            buttonOk: false,
                            title: this.translate.instant('Warning'),
                            message: this.translate.instant('DiscardQuestion_Text'),
                            type: NotificationType.information,
                        }),
                    );
                    discardPreviousImages = buttonClick == ButtonClick.yes;
                }
                break;
            case EstensaModel.ImageActionType.Append:
                discardPreviousImages = false;
                break;
            case EstensaModel.ImageActionType.Discard:
                discardPreviousImages = true;
                break;
        }

        const openParams: ImagingOpenParams = {
            discardPreviousImages,
            appendToPreviousImages: !discardPreviousImages,
            useAlternativeUrl: false,
        };

        // Serve cloneDeep per non lockare l'item nello stato, altriemnti quando faccio la select scoppia tutto
        this.store.dispatch(seOpenImagesByEprEvent({ eprEvent: [cloneDeep(item)], credentials: null, openParams }));
    }
}
